import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Enterprise } from '../../types/enterprise';
import * as api from '../../services/api';

// Define the state structure for enterprises
interface EnterpriseState {
  enterprises: Enterprise[];
  followedEnterprises: string[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
}

// Async thunks for API calls
export const fetchEnterprises = createAsyncThunk(
  'enterprises/fetchEnterprises',
  async (_, { rejectWithValue }) => {
    try {
      return await api.fetchEnterprises();
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const followEnterpriseAsync = createAsyncThunk(
  'enterprises/followEnterprise',
  async (enterpriseId: string, { rejectWithValue }) => {
    try {
      // In a real app, you would get the userId from auth state
      const userId = 'current-user-id';
      await api.followEnterprise(enterpriseId, userId);
      return enterpriseId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const unfollowEnterpriseAsync = createAsyncThunk(
  'enterprises/unfollowEnterprise',
  async (enterpriseId: string, { rejectWithValue }) => {
    try {
      // In a real app, you would get the userId from auth state
      const userId = 'current-user-id';
      await api.unfollowEnterprise(enterpriseId, userId);
      return enterpriseId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Empty initial data (will be filled by API call)
const initialEnterprises: Enterprise[] = [
  {
    id: 'kayak',
    name: 'Kayak',
    category: 'Travel',
    logo: '/images/kayak.png',
    topEarnings: 300000,
    creators: 1200,
    active: 451
  },
  {
    id: 'mindmatrix',
    name: 'Mindmatrix',
    category: 'B2B',
    logo: '/images/mindmatrix.png',
    topEarnings: 21000,
    creators: 45,
    active: 23
  },
  {
    id: 'emirates',
    name: 'Emirates',
    category: 'Luxury',
    logo: '/images/emirates.png',
    topEarnings: 10000,
    creators: 1200,
    active: 451
  }
];

// Initial state
const initialState: EnterpriseState = {
  enterprises: initialEnterprises,
  followedEnterprises: [],
  loading: false,
  error: null,
  searchQuery: ''
};

// Create the slice
export const enterpriseSlice = createSlice({
  name: 'enterprises',
  initialState,
  reducers: {
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Set enterprises
    setEnterprises: (state, action: PayloadAction<Enterprise[]>) => {
      state.enterprises = action.payload;
    },
    
    // Toggle follow status for an enterprise
    toggleFollowEnterprise: (state, action: PayloadAction<string>) => {
      const enterpriseId = action.payload;
      if (state.followedEnterprises.includes(enterpriseId)) {
        state.followedEnterprises = state.followedEnterprises.filter(id => id !== enterpriseId);
      } else {
        state.followedEnterprises.push(enterpriseId);
      }
    },
    
    // Set search query
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchEnterprises
      .addCase(fetchEnterprises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnterprises.fulfilled, (state, action) => {
        state.loading = false;
        state.enterprises = action.payload;
      })
      .addCase(fetchEnterprises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Handle followEnterpriseAsync
      .addCase(followEnterpriseAsync.fulfilled, (state, action) => {
        const enterpriseId = action.payload;
        if (!state.followedEnterprises.includes(enterpriseId)) {
          state.followedEnterprises.push(enterpriseId);
        }
      })
      
      // Handle unfollowEnterpriseAsync
      .addCase(unfollowEnterpriseAsync.fulfilled, (state, action) => {
        const enterpriseId = action.payload;
        state.followedEnterprises = state.followedEnterprises.filter(
          id => id !== enterpriseId
        );
      });
  }
});

// Export actions
export const { 
  setLoading, 
  setError, 
  setEnterprises, 
  toggleFollowEnterprise,
  setSearchQuery
} = enterpriseSlice.actions;

// Export selectors
export const selectEnterprises = (state: { enterprises: EnterpriseState }) => state.enterprises.enterprises;
export const selectFollowedEnterprises = (state: { enterprises: EnterpriseState }) => state.enterprises.followedEnterprises;
export const selectLoading = (state: { enterprises: EnterpriseState }) => state.enterprises.loading;
export const selectError = (state: { enterprises: EnterpriseState }) => state.enterprises.error;
export const selectSearchQuery = (state: { enterprises: EnterpriseState }) => state.enterprises.searchQuery;

// Export filtered enterprises selector
export const selectFilteredEnterprises = (state: { enterprises: EnterpriseState }) => {
  const { enterprises, searchQuery } = state.enterprises;
  if (!searchQuery) return enterprises;
  
  return enterprises.filter(enterprise => 
    enterprise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    enterprise.category.toLowerCase().includes(searchQuery.toLowerCase())
  );
};

export default enterpriseSlice.reducer;
